import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductsApi } from '@up2stay/up2promo-api-client';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  ghostAccentIconTileClass,
  nestedSecondarySurfaceClass,
  sectionAccentIconClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePhysicalBusinessCards } from '@/core/hooks/use-physical-business-cards';
import { productsQueryKeys } from '@/core/query-keys/products-query-keys';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

type PhysicalCardsDict = {
  title?: string;
  description?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  linkCard?: string;
  linkAnother?: string;
  linkedStatus?: string;
  virtualQrHint?: string;
  release?: string;
  releaseTitle?: string;
  releaseDescription?: string;
  releaseConfirm?: string;
  releaseCancel?: string;
  releaseSuccess?: string;
  releaseError?: string;
  cardFallbackLabel?: string;
};

type PhysicalCardsSectionProps = {
  dict?: PhysicalCardsDict;
  enabled?: boolean;
};

export function PhysicalCardsSection({
  dict,
  enabled = true,
}: PhysicalCardsSectionProps) {
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const { cards, isLoading, refetch } = usePhysicalBusinessCards({ enabled });
  const [releaseTarget, setReleaseTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const releaseMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!currentWorkspace?.id) throw new Error('Missing workspace');
      return ProductsApi.remove({
        path: { workspaceId: currentWorkspace.id, id },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['physical-business-cards', currentWorkspace?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.all(currentWorkspace?.id),
      });
      setReleaseTarget(null);
      if (dict?.releaseSuccess) toast.success(dict.releaseSuccess);
      void refetch();
    },
    onError: () => {
      if (dict?.releaseError) toast.error(dict.releaseError);
    },
  });

  const linkCardPath = shellPaths.profilesLinkCard;
  const linkedLabel = dict?.linkedStatus ?? 'Linked';

  return (
    <>
      <Card className="min-w-0">
        <CardHeader className="gap-1.5 pb-0 max-md:p-5 md:p-6">
          <CardTitle className="flex items-center gap-2.5 text-[15.5px] font-bold leading-tight max-md:gap-2.5 max-md:text-[15px]">
            <CreditCard
              className={cn(sectionAccentIconClass, 'max-md:size-[18px]')}
            />
            {dict?.title ?? 'Physical cards'}
          </CardTitle>
          <p className="text-[13px] leading-normal text-muted-foreground max-md:text-[12.5px] max-md:leading-[1.55]">
            {dict?.description ??
              'Link physical business cards to your profile. After linking, they automatically redirect to your digital profile.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3 p-5 pt-4 md:space-y-3 md:p-6 md:pt-[18px]">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-11 w-full rounded-[11px]" />
            </div>
          ) : cards.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border/70 bg-secondary/40 px-4 py-8 text-center">
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-[10px]',
                  ghostAccentIconTileClass,
                )}
              >
                <CreditCard className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {dict?.emptyTitle ?? 'No physical cards linked yet'}
                </p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {dict?.emptyDescription ??
                    'Scan your business card to connect it to your profile.'}
                </p>
              </div>
              <Button
                size="md"
                className="w-full sm:w-auto"
                data-guidance-target="profile-link-card"
                onClick={() => navigateShell(linkCardPath)}
              >
                <Plus className="mr-2 h-4 w-4" />
                {dict?.linkCard ?? 'Link physical card'}
              </Button>
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                {cards.map((card) => {
                  const label =
                    card.label?.trim() ||
                    dict?.cardFallbackLabel ||
                    'Business card';
                  return (
                    <li
                      key={card.id}
                      className={cn(
                        'flex items-center gap-3.5',
                        nestedSecondarySurfaceClass,
                        isMobile
                          ? 'gap-3 rounded-[13px] px-3.5 py-3'
                          : 'rounded-xl px-4 py-[15px]',
                      )}
                    >
                      <div
                        className={cn(
                          'flex flex-shrink-0 items-center justify-center rounded-[10px]',
                          ghostAccentIconTileClass,
                          isMobile ? 'size-[38px]' : 'size-10',
                        )}
                      >
                        <CreditCard
                          className={isMobile ? 'size-[18px]' : 'size-[19px]'}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        {isMobile ? (
                          <div className="flex min-w-0 items-center gap-1.5">
                            <p className="truncate text-[13.5px] font-semibold text-foreground">
                              {label}
                            </p>
                            <Badge
                              variant="success"
                              className="px-1.5 py-0.5 text-[10px] font-semibold"
                            >
                              {linkedLabel}
                            </Badge>
                          </div>
                        ) : (
                          <p className="truncate text-sm font-semibold text-foreground">
                            {label}
                          </p>
                        )}
                        {card.hash ? (
                          <p
                            className={cn(
                              'truncate font-mono text-muted-foreground',
                              isMobile
                                ? 'mt-0.5 text-[11px]'
                                : 'mt-0.5 text-xs',
                            )}
                          >
                            {card.hash}
                          </p>
                        ) : null}
                      </div>
                      {!isMobile ? (
                        <Badge
                          variant="success"
                          className="px-2.5 py-1 text-[11.5px] font-semibold"
                        >
                          {linkedLabel}
                        </Badge>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={cn(
                          'flex-shrink-0 text-muted-foreground hover:text-destructive',
                          isMobile
                            ? 'size-8 rounded-[9px]'
                            : 'size-[34px] rounded-[9px]',
                        )}
                        aria-label={dict?.release ?? 'Release card'}
                        onClick={() =>
                          setReleaseTarget({
                            id: card.id,
                            label,
                          })
                        }
                      >
                        <Trash2
                          className={isMobile ? 'size-[15px]' : 'size-4'}
                        />
                      </Button>
                    </li>
                  );
                })}
              </ul>
              <Button
                variant="dashed"
                size="md"
                className="mt-3 w-full min-h-11 text-[13.5px] font-semibold max-md:mt-[11px] max-md:text-[13px]"
                onClick={() => navigateShell(linkCardPath)}
              >
                <Plus className="mr-2 h-4 w-4" />
                {dict?.linkAnother ?? 'Link another card'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!releaseTarget}
        onOpenChange={(open) => {
          if (!open) setReleaseTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dict?.releaseTitle ?? 'Release this card?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dict?.releaseDescription ??
                'The card will be unlinked from your profile and can be set up again later.'}
              {releaseTarget?.label ? (
                <span className="mt-2 block font-medium text-foreground">
                  {releaseTarget.label}
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {dict?.releaseCancel ?? 'Cancel'}
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={releaseMutation.isPending}
              onClick={() => {
                if (releaseTarget) releaseMutation.mutate(releaseTarget.id);
              }}
            >
              {releaseMutation.isPending
                ? (dict?.release ?? 'Release')
                : (dict?.releaseConfirm ?? 'Release card')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
